import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  LinearProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Speed as SpeedIcon,
  Timeline as TimelineIcon,
  AttachMoney as MoneyIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import api from '../../services/api';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  PieChart,
  Pie
} from 'recharts';

const SlaCostDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('24h');
  const [latencyDialogOpen, setLatencyDialogOpen] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [latencyTrend, setLatencyTrend] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [timeRange]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/admin/smpp/sla-cost/dashboard?timeRange=${timeRange}`);
      setDashboardData(response.data?.data || null);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      setError(error.response?.data?.error || error.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Safe accessors for nested data
  const summary = dashboardData?.summary || {};
  const heatmapData = dashboardData?.heatmapData || [];
  const routeMetrics = dashboardData?.routeMetrics || [];
  const operatorMetrics = dashboardData?.operatorMetrics || [];
  const safeNum = (val) => (typeof val === 'number' && !isNaN(val)) ? val : 0;

  const handleViewLatencyTrend = async (routeId) => {
    try {
      const response = await api.get(`/admin/smpp/sla-cost/routes/${routeId}/latency-trend?hours=24`);
      setLatencyTrend(response.data.data);
      setSelectedRoute(routeId);
      setLatencyDialogOpen(true);
    } catch (error) {
      console.error('Failed to fetch latency trend:', error);
    }
  };

  const getSlaStatusColor = (status) => {
    switch (status) {
      case 'PASS': return 'success';
      case 'WARN': return 'warning';
      case 'FAIL': return 'error';
      default: return 'default';
    }
  };

  const getSlaStatusIcon = (status) => {
    switch (status) {
      case 'PASS': return <CheckCircleIcon color="success" />;
      case 'WARN': return <WarningIcon color="warning" />;
      case 'FAIL': return <ErrorIcon color="error" />;
      default: return <InfoIcon />;
    }
  };

  const heatmapColor = (health) => {
    if (health >= 90) return '#4caf50';
    if (health >= 70) return '#ff9800';
    return '#f44336';
  };

  const pieData = dashboardData ? [
    { name: 'Healthy', value: safeNum(summary.healthyRoutes), color: '#4caf50' },
    { name: 'Warning', value: safeNum(summary.warningRoutes), color: '#ff9800' },
    { name: 'Critical', value: safeNum(summary.criticalRoutes), color: '#f44336' }
  ] : [];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">SMPP SLA & Cost Dashboard</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <MenuItem value="1h">Last Hour</MenuItem>
              <MenuItem value="24h">Last 24 Hours</MenuItem>
              <MenuItem value="7d">Last 7 Days</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary">
            Last updated: {lastUpdated ? lastUpdated.toLocaleTimeString() : 'Never'}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchDashboardData}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body2">{error}</Typography>
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Reporting-only dashboard. No real-time routing changes are made from this view.
          Data is aggregated from the reporting database.
        </Typography>
      </Alert>

      {!dashboardData && !loading && !error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            No SLA/Cost data available for the selected time range. This may be because no SMPP routes are configured or no traffic has been processed yet.
          </Typography>
        </Alert>
      )}

      {dashboardData && (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total TPS
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SpeedIcon fontSize="small" />
                    <Typography variant="h4">
                      {safeNum(summary.totalTPS).toFixed(1)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Daily Volume
                  </Typography>
                  <Typography variant="h4">
                    {(safeNum(summary.totalVolume) / 1000000).toFixed(2)}M
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Avg SLA
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={safeNum(summary.avgSLA)}
                      sx={{ width: 100 }}
                    />
                    <Typography variant="h6">
                      {safeNum(summary.avgSLA).toFixed(1)}%
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Cost
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MoneyIcon fontSize="small" />
                    <Typography variant="h4">
                      ${safeNum(summary.totalCost).toFixed(2)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Route Health Pie Chart */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Route Health Distribution</Typography>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Route Health Heatmap */}
            <Grid item xs={12} sm={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Route Health Heatmap</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {heatmapData.length === 0 && (
                      <Typography variant="body2" color="text.secondary">No heatmap data available</Typography>
                    )}
                    {heatmapData.map((point) => (
                      <Tooltip
                        key={point.routeId}
                        title={`${point.routeName || 'Unknown'}: Health ${safeNum(point.health).toFixed(1)}%, Latency ${safeNum(point.latency)}ms, Error Rate ${safeNum(point.errorRate).toFixed(2)}%`}
                      >
                        <Box
                          sx={{
                            width: 60,
                            height: 40,
                            backgroundColor: heatmapColor(safeNum(point.health)),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 1,
                            cursor: 'pointer',
                            '&:hover': { opacity: 0.8 }
                          }}
                        >
                          <Typography variant="caption" sx={{ color: 'white' }}>
                            {safeNum(point.health).toFixed(0)}%
                          </Typography>
                        </Box>
                      </Tooltip>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Route Metrics Table */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Route SLA Metrics</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Route Name</TableCell>
                      <TableCell align="right">Submit TPS</TableCell>
                      <TableCell align="right">Avg Latency</TableCell>
                      <TableCell align="right">P95 Latency</TableCell>
                      <TableCell align="right">P99 Latency</TableCell>
                      <TableCell align="right">Error Rate</TableCell>
                      <TableCell align="right">DLR Success</TableCell>
                      <TableCell>SLA Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {routeMetrics.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>No route metrics available</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {routeMetrics.map((route) => (
                      <TableRow key={route.routeId}>
                        <TableCell>{route.routeName || 'Unknown'}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {safeNum(route.submitTPS).toFixed(1)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {safeNum(route.avgSubmitLatency).toFixed(0)}ms
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {safeNum(route.p95Latency).toFixed(0)}ms
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {safeNum(route.p99Latency).toFixed(0)}ms
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color={safeNum(route.errorRate) > 2 ? 'error' : 'text.secondary'}>
                            {safeNum(route.errorRate).toFixed(2)}%
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color={safeNum(route.dlrSuccessRate) < 98 ? 'error' : 'text.secondary'}>
                            {safeNum(route.dlrSuccessRate).toFixed(1)}%
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getSlaStatusIcon(route.slaStatus)}
                            <Chip
                              label={route.slaStatus}
                              color={getSlaStatusColor(route.slaStatus)}
                              size="small"
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Latency Trend">
                            <IconButton size="small" onClick={() => handleViewLatencyTrend(route.routeId)}>
                              <TimelineIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Operator Cost Metrics */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Operator Cost Metrics</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Operator Name</TableCell>
                      <TableCell align="right">Daily Volume</TableCell>
                      <TableCell align="right">Cost per SMS</TableCell>
                      <TableCell align="right">Total Cost</TableCell>
                      <TableCell align="right">Margin</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {operatorMetrics.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>No operator cost data available</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {operatorMetrics.map((operator) => (
                      <TableRow key={operator.operatorId}>
                        <TableCell>{operator.operatorName || 'Unknown'}</TableCell>
                        <TableCell align="right">
                          {(safeNum(operator.dailyVolume) / 1000).toFixed(0)}K
                        </TableCell>
                        <TableCell align="right">
                          ${safeNum(operator.costPerSMS).toFixed(4)}
                        </TableCell>
                        <TableCell align="right">
                          ${safeNum(operator.totalCost).toFixed(2)}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color={safeNum(operator.margin) < 0 ? 'error' : 'success'}>
                            {safeNum(operator.margin) > 0 ? '+' : ''}{safeNum(operator.margin).toFixed(2)}%
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}

      {/* Latency Trend Dialog */}
      <Dialog 
        open={latencyDialogOpen} 
        onClose={() => setLatencyDialogOpen(false)} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>Latency Trend (Last 24 Hours)</DialogTitle>
        <DialogContent>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={latencyTrend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="hour" 
                tickFormatter={(value) => new Date(value).toLocaleTimeString()}
              />
              <YAxis />
              <RechartsTooltip 
                labelFormatter={(value) => new Date(value).toLocaleString()}
              />
              <Line 
                type="monotone" 
                dataKey="avg_latency" 
                stroke="#8884d8" 
                name="Avg Latency (ms)"
              />
              <Line 
                type="monotone" 
                dataKey="p95_latency" 
                stroke="#82ca9d" 
                name="P95 Latency (ms)"
              />
              <Line 
                type="monotone" 
                dataKey="p99_latency" 
                stroke="#ffc658" 
                name="P99 Latency (ms)"
              />
            </LineChart>
          </ResponsiveContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLatencyDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SlaCostDashboard;
